import * as React from 'react';
import PageModel from '@app/Model/PageModel';
import { Card, CardBody, Split, SplitItem, Switch, Title } from '@patternfly/react-core';
import UserModel from '@app/Model/UserModel';
import NotificationFormInterface from '@app/Interface/NotificationFormInterface';
import NotificationFormModel from '@app/Model/NotificationFormModel';
import SendNotificationField from './SendNotificationField';
import { UserContext } from '@app/Context/UserContext';
import SaveUserNotificationSettingsModel from '@app/Model/SaveUserNotificationSettingsModel';
import UserNotificationSettingsHelper from '@app/Service/UserNotificationSettingsHelper';
import NotificationOptIns from './NotificationOptIns';

interface NotificationSettingsProps {
    pageData: PageModel;
    currentUser: UserModel;
    handleEditSettingButton: (value: boolean, type: "Email" | "Text") => void;
}

const NotificationSettings: React.FC<NotificationSettingsProps> = ({ pageData, currentUser, handleEditSettingButton }) => {

    const initialFields = new NotificationFormModel();
    initialFields.enableNotifications = currentUser.enableNotifications;
    initialFields.notificationEmailAddress = currentUser.notificationEmailAddress;
    initialFields.notificationPhoneNumber = currentUser.notificationPhoneNumber;
    initialFields.sendEmailNotifications = currentUser.sendEmailNotifications;
    initialFields.sendSmsNotifications = currentUser.sendSmsNotifications;

    const [formFields, setFormFields] = React.useState<NotificationFormInterface>(initialFields);

    const { userDispatch } = React.useContext(UserContext);

    /**
     * Handler to change the isChecked state for sending a notification
     * @param checked 
     * @param event 
     */
    const handleChange = (checked: boolean, event: React.FormEvent<HTMLInputElement>) => {

        // handle change.
        const fieldId = event.currentTarget.id;

        const updatedFields = { ...formFields };

        const updatedUser = {...currentUser};

        switch (fieldId) {
            case "enableNotifications":
                updatedFields.enableNotifications = checked;
                updatedUser.enableNotifications = checked;
                break;

            case "sendEmailNotifications":
                updatedFields.sendEmailNotifications = checked;
                updatedUser.sendEmailNotifications = checked;
                break;

            case "sendSmsNotifications":
                updatedFields.sendSmsNotifications = checked;
                updatedUser.sendSmsNotifications = checked;
                break;

            default:
                break;
        }

        // update user context
        userDispatch({
            type: "UPDATE",
            payload: updatedUser
        });

        // send middleware request to update user. DON'T wait
        try {

            const updatedSettings = new SaveUserNotificationSettingsModel();
            updatedSettings.userId = currentUser.id;
            updatedSettings.enableNotifications = updatedFields.enableNotifications;
            updatedSettings.sendEmailNotifications = updatedFields.sendEmailNotifications;
            updatedSettings.notificationEmailAddress = updatedFields.notificationEmailAddress;
            updatedSettings.sendSMSNotifications = updatedFields.sendSmsNotifications;
            updatedSettings.notificationPhoneNumber = updatedFields.notificationPhoneNumber;

            
            // no async await
            UserNotificationSettingsHelper.saveUserNotificationSettings(updatedSettings);

        } catch (error) {
            console.group("NotificationSettings update");
            console.info("checked: ", checked);
            console.info("fieldId: ", fieldId);
            console.info("error", error);
            console.groupEnd();
        }

        setFormFields(updatedFields);
    };

    return (
        <>
            <div className="pf-u-mb-md">{pageData.acf.allow_notifications_section_description}</div>

            <Card className="pf-u-mb-md">
                <CardBody>
                    <Split>
                        <SplitItem isFilled>
                            <Title headingLevel="h2">Allow Notifications</Title>
                        </SplitItem>
                        <SplitItem>
                            <Switch
                                id="enableNotifications"
                                aria-label="Allow Notifications"
                                isChecked={formFields.enableNotifications}
                                onChange={handleChange}
                                isReversed
                            />
                        </SplitItem>
                    </Split>
                </CardBody>
            </Card>

            {formFields.enableNotifications && <>
                <Title headingLevel="h2">Notification Settings</Title>

                <p className="pf-u-mb-md">{pageData.acf.notification_settings_section_description}</p>

                <SendNotificationField
                    type="Email"
                    fieldId="sendEmailNotifications"
                    isChecked={formFields.sendEmailNotifications}
                    notificationValue={formFields.notificationEmailAddress}
                    handleChange={handleChange}
                    handleEditSettingButton={handleEditSettingButton}
                    showEditButton={true}
                />

                <SendNotificationField
                    type="Text"
                    fieldId="sendSmsNotifications"
                    isChecked={formFields.sendSmsNotifications}
                    notificationValue={formFields.notificationPhoneNumber}
                    handleChange={handleChange}
                    handleEditSettingButton={handleEditSettingButton}
                    showEditButton={true}
                />

                <NotificationOptIns currentUser={currentUser} />

            </>}

        </>
    );

};

export default NotificationSettings;